import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { useTheme } from '../services/ThemeContext';
import AddClientModal from './Modals/AddClientModal';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getHomeLink = () => {
    if (!user) return '/';
    if (user.role === 'coach') return '/coach/dashboard';
    if (user.role === 'client') return '/user/coaches';
    if (user.role === 'admin') return '/admin/coaches';
    return '/';
  };

  return (
    <>
      <AddClientModal isOpen={isAddClientOpen} onClose={() => setIsAddClientOpen(false)} />
      <nav className="bg-[var(--surface-white)] border-b border-[var(--border-light)] shadow-sm relative z-40 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to={getHomeLink()} className="flex items-center space-x-2">
              <span className="material-icons text-tangerine text-2xl md:text-3xl">change_history</span>
              <span className="font-bold text-lg md:text-xl tracking-tight text-[var(--text-primary)]">Ally AI</span>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <div className="flex items-center space-x-2">
                  <span className="text-[var(--text-primary)] font-bold">{user.name}</span>
                  <div className="text-tangerine text-xs uppercase font-bold px-2 py-1 bg-tangerine/10 rounded-lg border border-tangerine/20">
                    {user.role}
                  </div>
                </div>
                {user.role === 'coach' && (
                  <>
                    <Link
                      to="/coach/dashboard"
                      className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/coach/knowledge"
                      className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                    >
                      Knowledge
                    </Link>
                    <Link
                      to="/coach/settings"
                      className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                    >
                      Settings
                    </Link>
                    <Link
                      to="/coach/finances"
                      className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                    >
                      Finances
                    </Link>
                    <Link
                      to="/coach/referrals"
                      className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                    >
                      Referrals
                    </Link>
                    <button 
                      onClick={() => setIsAddClientOpen(true)}
                      className="ml-2 flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-[var(--tangerine)] text-white rounded-xl hover:bg-[#d97700] transition-colors shadow-md shadow-orange-500/20"
                    >
                      <span className="material-symbols-outlined text-lg">person_add</span>
                      Add Client
                    </button>
                  </>
                )}
                {user.role === 'client' && (
                  <>
                    <Link
                      to="/user/coaches"
                      className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                    >
                      Coaches
                    </Link>
                    <Link
                      to="/user/settings"
                      className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                    >
                      Settings
                    </Link>
                  </>
                )}
                {user.role === 'admin' && (
                  <>
                    <Link
                      to="/admin/coaches"
                      className="text-[var(--tangerine)] font-bold px-3 py-2 flex items-center gap-1"
                    >
                      <span className="material-symbols-outlined text-lg">admin_panel_settings</span>
                      Admin Panel
                    </Link>
                  </>
                )}

                <button
                  onClick={handleLogout}
                  className="bg-[var(--surface-white)] border border-[var(--border-light)] hover:border-red-500 hover:text-red-500 text-[var(--text-primary)] px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm hover:shadow-md ml-2"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-[var(--text-secondary)] hover:text-tangerine px-3 py-2 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-tangerine hover:bg-tangerine/90 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-md shadow-tangerine/20 transition-all hover:-translate-y-0.5"
                >
                  Register
                </Link>
              </>
            )}

            <button
              onClick={toggleTheme}
              className="ml-2 p-2 rounded-xl bg-[var(--off-white)] border border-[var(--border-light)] text-[var(--text-secondary)] hover:text-tangerine transition-all shadow-sm hover:shadow-md"
              aria-label="Toggle night mode"
            >
              <span className="material-symbols-outlined flex items-center justify-center">
                {theme === 'light' ? 'dark_mode' : 'light_mode'}
              </span>
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-[var(--text-secondary)]"
            >
              <span className="material-symbols-outlined">
                {theme === 'light' ? 'dark_mode' : 'light_mode'}
              </span>
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-[var(--surface-white)] border-t border-[var(--border-light)] transition-colors duration-300">
              {user ? (
                <>
                  <div className="px-3 py-3 border-b border-[var(--border-light)]">
                    <div className="text-[var(--text-primary)] font-bold">{user.name}</div>
                    <div className="text-tangerine text-xs uppercase font-bold px-2 py-1 bg-tangerine/10 rounded-lg border border-tangerine/20 inline-block mt-1">
                      {user.role}
                    </div>
                  </div>
                  {user.role === 'coach' && (
                    <>
                      <Link
                        to="/coach/dashboard"
                        className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/coach/knowledge"
                        className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        Knowledge
                      </Link>
                      <Link
                        to="/coach/settings"
                        className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        Settings
                      </Link>
                      <Link
                        to="/coach/finances"
                        className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        Finances
                      </Link>
                      <Link
                        to="/coach/referrals"
                        className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        Referrals
                      </Link>
                      <button 
                        onClick={() => { setIsAddClientOpen(true); setIsOpen(false); }}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-[var(--tangerine)] font-bold hover:bg-orange-50 bg-orange-50/50 mt-1"
                      >
                        <span className="material-symbols-outlined text-lg">person_add</span>
                        Add Client
                      </button>
                    </>
                  )}
                  {user.role === 'client' && (
                    <>
                      <Link
                        to="/user/coaches"
                        className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        Coaches
                      </Link>
                      <Link
                        to="/user/settings"
                        className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                        onClick={() => setIsOpen(false)}
                      >
                        Settings
                      </Link>
                    </>
                  )}
                  {user.role === 'admin' && (
                    <>
                      <Link
                        to="/admin/coaches"
                        className="block px-3 py-2 text-[var(--tangerine)] font-bold hover:bg-orange-50 bg-orange-50/50"
                        onClick={() => setIsOpen(false)}
                      >
                        Admin Panel
                      </Link>
                    </>
                  )}

                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-red-600 font-bold hover:bg-red-50 mt-2"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-gray-600 hover:text-tangerine hover:bg-gray-50"
                    onClick={() => setIsOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="block px-3 py-2 text-tangerine hover:bg-tangerine/10"
                    onClick={() => setIsOpen(false)}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar;
